import { getComputedColor } from '@/utils/color';

export const PLAYER_STATE = {
    playing: 'playing',
    paused: 'paused',
};

export const PLAYER_NEXT_STATE = {
    [PLAYER_STATE.playing]: PLAYER_STATE.paused,
    [PLAYER_STATE.paused]: PLAYER_STATE.playing,
};

export const PLAYER_STATE_ICON = {
    [PLAYER_STATE.playing]: 'pause',
    [PLAYER_STATE.paused]: 'play',
};

export const PLAYBACK_SPEED_MIN = 0.25;

export const PLAYBACK_SPEED_MAX = 4;

export const PLAYBACK_SPEED_STEP = 0.25;

export const PLAYBACK_SPEED_PRESET_OPTIONS = [0.25, 0.5, 1, 1.5, 2, 4];

export const MENU_VIEW = Object.freeze({
    PRESETS: 'presets',
    CUSTOM: 'custom',
});

/** @deprecated Use PLAYBACK_SPEED_PRESET_OPTIONS */
export const PLAYBACK_SPEED_OPTIONS = PLAYBACK_SPEED_PRESET_OPTIONS;

export const PRESET_COLORS = {
    white: 'var(--static-popover-background-base)',
    black: 'var(--static-code-block-background)',
    pink: 'var(--static-product-area-group-color-1)',
    magenta: 'var(--static-data-vis-magenta-vis-magenta-100)',
    redOrange: 'var(--static-data-vis-nps-nps-orange-80)',
    orange: 'var(--static-data-vis-orange-vis-orange-120)',
    teal: 'var(--static-data-vis-teal-vis-teal-120)',
    green: 'var(--static-status-success-fill)',
    blue: 'var(--static-data-vis-blue-vis-blue-130)',
    skyblue: 'var(--static-data-vis-blue-vis-blue-100)',
    purple: 'var(--static-data-vis-purple-vis-purple-120)',
    lavender: 'var(--static-data-vis-purple-vis-purple-100)',
};

export const EVENT_LOG_WIDTH = 450;

export const PLAYER_MIN_WIDTH = 748;

/** Saved user preference: replay-relative clock vs session wall-clock time */
export const REPLAY_TIME_DISPLAY_MODE = {
    replay: 'replay',
    user: 'user',
};

// Which developer tools panel a dev-log-filtered payload came from.
export const DEV_LOG_TYPE = {
    console: 'console',
    network: 'network',
};

export const DEFAULT_PLAYER_SETTINGS = {
    speed: 1,
    showAllFrustrations: false,
    showDeveloperTools: false,
    activeDeveloperToolsTab: 'console',
    devToolsWidth: EVENT_LOG_WIDTH,
    skipInactive: true,
    mouseTail: true,
    autoStart: true,
    pathColor: getComputedColor(PRESET_COLORS.pink),
    visitorAndAccountMetadataExpanded: true,
    metadataToShow: [
        { kind: 'visitor', field: 'visitor.auto.accountid' },
        { kind: 'visitor', field: 'visitor.auto.firstvisit' },
        { kind: 'visitor', field: 'visitor.auto.lastvisit' },
    ],
    replayTimeDisplayMode: REPLAY_TIME_DISPLAY_MODE.replay,
};

export const DEFAULT_TIME_SKIP_MS = 10000;

export const PLAYER_BLOCK_ELEMENT_CLASS = 'pendo_replay_blocked_element';

export const IFRAME_CONTAINER_BORDER_WIDTH = 1;

export const CONTENT_STATE = {
    LOADING: 'loading',
    SUCCESS: 'success',
    EMPTY: 'empty',
    ERROR: 'error',
    EXPIRED: 'expired',
};

// composable defaults
export const DEFAULT_REPLAYER_STATES = {
    metadata: { startTime: 0, totalTime: 0 },
    inactivePeriods: [],
    sessionActivities: [],
    currentTime: 0,
    clipHighlight: { start: null, end: null },
};

// anything in DEFAULT_PLAYER_STATES should be reset in `resetState()`
export const DEFAULT_PLAYER_STATES = {
    playerState: PLAYER_STATE.paused,
    isSkipping: false,
    wasPlaying: null,
    replayEnded: false,
    isFullscreen: false,
    playbackPerf: false,
    isAlreadyLoaded: false,
    searchTimestamp: { timestamp: 0 },
    batchTimeRange: { batchStartTime: 0, batchEndTime: 0 },
    isSwappingSession: false,
    resumeSessionSwapOffset: null,
    sessionIndex: 0,
    activeSessionNumber: null,
};

/* eslint-disable id-length */
export const KEYBOARD_KEYCODES = {
    SPACE: 32,
    LEFT_ARROW: 37,
    RIGHT_ARROW: 39,
    C: 67,
    F: 70,
    J: 74,
    K: 75,
    L: 76,
};

export const REPLAY_TYPE = {
    replay: 'replay',
    savedClip: 'saved clip',
};

export const DASHBOARD = 'dashboard';

export const PLAYLIST = 'playlist';

export const CLIP_POPOVER_TYPES = {
    CREATE_ISSUE: 'createIssue',
    SHARE: 'share',
    ADD_TO_PLAYLIST: 'addToPlaylist',
};

export const LIVE_CLIP_EXPIRED_ERRORS = [
    'no session replay clip events found',
    'session replay clip events missing for some requested recordings',
];

export const AI_SUMMARY_NO_EVENTS_ERROR = 'no session replay events found';

export const FRUSTRATION_TYPE_DURATIONS = {
    uTurn: 7000,
};

export const EVENT_BUFFER_IN_MS = 10 * 1000; // 10 second buffer used for playback perf

export const REPLAY_VIDEO_PLAYER_ID = 'pendo-replay-video-player';

export const LOG_TRACE = 'devLogTrace';

export const LOG_MESSAGE = 'devLogMessage';

export const LOG_METHOD = 'devLogMethod';

export const LOG_STATUS_CODE = 'devLogStatusCode';

export const LOG_REQUEST_URL = 'devLogRequestUrl';

export const LOG_TYPES = [
    {
        label: 'Log',
        prop: 'info',
    },
    {
        label: 'Warning',
        prop: 'warn',
    },
    {
        label: 'Error',
        prop: 'error',
    },
];

export const REPLAY_ACTION_TRACK_EVENT = 'replayAction';

export const REPLAY_ACTIONS = {
    saveClip: 'saveClip',
    createIssue: 'createIssue',
    createAiIssue: 'createAiIssue',
    addToPlaylist: 'addToPlaylist',
};
